import { ReactNode, useMemo } from 'react';
import TableCard, { TableColumn } from '../../../components/common/TableCard';

type Staff = {
  user_id?: string | null;
  email?: string | null;
  role?: string | null;
  full_name?: string | null;
  org_staff_id?: number | string | null;
  acc_staff_id?: number | string | null;
};

type StaffTableProps<T extends Staff> = {
  title?: string;
  staff: T[];
  onRowClick?: (member: T) => void;
  renderActions?: (member: T) => ReactNode;
  getRowKey?: (member: T, index: number) => string | number;
  actionButtons?: ReactNode;
};

export default function StaffTable<T extends Staff>({
  title = 'Staff',
  staff,
  onRowClick,
  renderActions,
  getRowKey,
  actionButtons,
}: StaffTableProps<T>): JSX.Element {
  const columns = useMemo<Array<TableColumn<T>>>(() => {
    const cols: Array<TableColumn<T>> = [
      { label: 'Name', render: (m) => (m as any).full_name || m.user_id || '-' },
      { label: 'Email', render: (m) => m.email || 'N/A' },
      { label: 'Role', render: (m) => m.role || '-' },
    ];

    if (renderActions) {
      cols.push({
        label: 'Actions',
        render: (m) => renderActions(m),
      });
    }
    return cols;
  }, [renderActions]);

  const rowKeyGetter =
    getRowKey ??
    ((m: T, i: number) => (m as any).org_staff_id ?? (m as any).acc_staff_id ?? `${m.user_id ?? 'user'}-${i}`);

  return (
    <TableCard
      title={title}
      rows={staff}
      columns={columns}
      getRowKey={rowKeyGetter}
      onRowClick={onRowClick}
      actionButtons={actionButtons}
    />
  );
}
